import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ClipboardCheck, Eye, Truck, XCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import ogclTransferService from '../../services/ogclTransferService.js';
import OgclReviewConfirmModal from './OgclReviewConfirmModal.js';
import ItclActionNoteModal from './ItclActionNoteModal.js';
import ItclShipmentActionsMenu, { itclMenuFlags } from './ItclShipmentActionsMenu.js';
import { formatUsernameLocation } from '../../utils/userDisplay';
import { canCreateMlSheet } from '../../utils/networkMode.js';
import { ITCL_REQUEST_DETAIL_ERROR } from './itclRequestUi.js';

const badgeClass = (status) => ({
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  APPROVED: 'bg-green-50 text-green-700 border-green-200',
  REJECTED: 'bg-red-50 text-red-700 border-red-200',
  REVERTED: 'bg-orange-50 text-orange-800 border-orange-200',
  Booked: 'bg-blue-50 text-blue-700 border-blue-200',
  BOOKED: 'bg-blue-50 text-blue-700 border-blue-200',
  'In Transit': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Dispatched: 'bg-purple-50 text-purple-700 border-purple-200',
}[status] || 'bg-gray-50 text-gray-700 border-gray-200');

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMoney = (value) => {
  const amount = Number(value || 0);
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const paymentLabel = (mode) => {
  const key = String(mode || '').toLowerCase();
  if (key === 'to_pay') return 'To Pay';
  if (key === 'paid') return 'Paid';
  if (key === 'credit' || key === 'on_account') return 'Credit';
  return mode || '—';
};

const DetailItem = ({ label, children }) => (
  <div className="min-w-0 rounded-lg bg-gray-50/80 px-3 py-2.5 sm:bg-transparent sm:px-0 sm:py-0">
    <p className="text-[10px] sm:text-xs uppercase tracking-wide text-gray-500 mb-0.5 sm:mb-1">{label}</p>
    <div className="text-xs sm:text-sm font-medium text-secondary break-words">{children || '—'}</div>
  </div>
);

const ViewOgclTransferRequest = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [unloadingId, setUnloadingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [request, setRequest] = useState(null);
  const [reviewModal, setReviewModal] = useState({ open: false, action: 'approve' });
  const [noteModal, setNoteModal] = useState({ open: false, action: 'unload', shipmentIds: [] });
  const role = user?.role || user?.role_name;
  const canReview = Boolean(request?.can_review);

  const loadRequest = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ogclTransferService.get(requestId);
      const data = response.data?.data || response.data;
      if (!data?.request_id) {
        throw new Error('Request not found');
      }
      setRequest(data);
    } catch (error) {
      Alert.error(error.response?.data?.error || ITCL_REQUEST_DETAIL_ERROR);
      navigate('/ogcl-transfer-requests', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate, requestId]);

  useEffect(() => {
    loadRequest();
  }, [loadRequest]);

  const openReview = (action) => setReviewModal({ open: true, action });
  const closeReview = () => {
    if (acting) return;
    setReviewModal({ open: false, action: 'approve' });
  };

  const confirmReview = async (remarks) => {
    if (!request?.request_id) return;
    try {
      setActing(true);
      await ogclTransferService[reviewModal.action](request.request_id, { remarks });
      Alert.success(`Request ${reviewModal.action === 'approve' ? 'approved' : 'rejected'}`);
      setReviewModal({ open: false, action: 'approve' });
      await loadRequest();
    } catch (error) {
      Alert.error(error.response?.data?.error || `Failed to ${reviewModal.action} request`);
    } finally {
      setActing(false);
    }
  };

  const markUnloaded = async (shipmentId) => {
    const shipment = (request?.shipments || []).find((row) => row.shipment_id === shipmentId);
    const flags = shipment ? itclMenuFlags(shipment, role) : {};
    if (shipment && !flags.canUnload) {
      Alert.error(
        shipment.lock_reason
        || (shipment.on_ml_sheet
          ? 'Unload is not available after an ML sheet is created'
          : 'Unload is only available before this shipment is unloaded')
      );
      return;
    }
    if (!request?.request_id || !shipmentId) return;
    setNoteModal({ open: true, action: 'unload', shipmentIds: [shipmentId] });
  };

  const markReverted = (shipmentId) => {
    const shipment = (request?.shipments || []).find((row) => row.shipment_id === shipmentId);
    const flags = shipment ? itclMenuFlags(shipment, role) : {};
    if (shipment && !flags.canRevert) {
      Alert.error(
        shipment.lock_reason
        || 'Revert is available only after the shipment is unloaded'
      );
      return;
    }
    if (!request?.request_id || !shipmentId) return;
    setNoteModal({ open: true, action: 'revert', shipmentIds: [shipmentId] });
  };

  const markRemoved = (shipmentId) => {
    const shipment = (request?.shipments || []).find((row) => row.shipment_id === shipmentId);
    const flags = shipment ? itclMenuFlags(shipment, role) : {};
    if (shipment && !flags.canRemove) {
      Alert.error(shipment.lock_reason || 'This shipment cannot be removed from the ITCL request');
      return;
    }
    if (!request?.request_id || !shipmentId) return;
    setNoteModal({ open: true, action: 'remove', shipmentIds: [shipmentId] });
  };

  const markNote = (shipmentId) => {
    if (!request?.request_id || !shipmentId) return;
    setNoteModal({ open: true, action: 'note', shipmentIds: [shipmentId] });
  };

  const confirmNoteAction = async (note) => {
    if (!request?.request_id || !noteModal.shipmentIds?.length) return;
    const action = noteModal.action;
    const shipmentIds = noteModal.shipmentIds;
    if (action !== 'revert' && !String(note || '').trim()) {
      Alert.error('Enter a note before continuing');
      return;
    }
    try {
      setUnloadingId(shipmentIds.length === 1 ? shipmentIds[0] : 'all');
      const response = action === 'remove'
        ? await ogclTransferService.remove(request.request_id, { shipment_ids: shipmentIds, note })
        : action === 'revert'
          ? await ogclTransferService.revert(request.request_id, { shipment_ids: shipmentIds, note })
          : action === 'note'
            ? await ogclTransferService.note(request.request_id, { shipment_ids: shipmentIds, note })
            : await ogclTransferService.unload(request.request_id, { shipment_ids: shipmentIds, note });
      const data = response.data?.data || response.data;
      if (data?.request_id) setRequest(data);
      Alert.success(
        action === 'remove'
          ? `${shipmentIds.length === 1 ? shipmentIds[0] : 'Selected shipments'} removed from this ITCL request`
          : action === 'revert'
            ? `${shipmentIds.length === 1 ? shipmentIds[0] : 'Selected shipments'} reverted to pending unload`
            : action === 'note'
              ? `Note saved for ${shipmentIds.length === 1 ? shipmentIds[0] : 'selected shipments'}`
              : (shipmentIds.length === 1 ? `${shipmentIds[0]} marked unloaded` : 'All shipments marked unloaded'),
      );
      setNoteModal({ open: false, action: 'unload', shipmentIds: [] });
    } catch (error) {
      Alert.error(error.response?.data?.error || `Failed to ${action} shipment`);
    } finally {
      setUnloadingId(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="py-20">
          <Loader fullScreen={false} size="default" message="Loading ITCL request..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!request) return null;

  const shipments = Array.isArray(request.shipments) ? request.shipments : [];
  const activeShipments = shipments.filter((row) => !row.reverted);
  const totalFreight = shipments.reduce((sum, row) => sum + Number(row.freight_charges || 0), 0);
  const canManageUnload = Boolean(request.can_manage_unload) || Boolean(request.is_destination_user)
    || ['System Admin', 'Admin', 'Accountant'].includes(role);
  const showChecklist = request.status === 'APPROVED' && canManageUnload;
  const allUnloaded = Boolean(request.all_unloaded)
    || (activeShipments.length > 0 && activeShipments.every((row) => row.unloaded));

  return (
    <DashboardLayout>
      <div className="w-full max-w-full min-w-0 mx-auto overflow-x-hidden space-y-4 sm:space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between min-w-0">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-secondary flex items-start gap-2 min-w-0">
              <Truck className="w-5 h-5 sm:w-6 sm:h-6 shrink-0 mt-0.5" />
              <span className="break-all leading-snug">{request.request_id}</span>
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">
              {request.from_hub_name || request.from_hub_id} → {request.to_hub_name || request.to_hub_id}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:justify-end shrink-0 w-full sm:w-auto">
            <span className={`inline-flex px-2.5 sm:px-3 py-1 rounded-full border text-[10px] sm:text-xs font-semibold ${badgeClass(request.status)}`}>
              {request.status}
            </span>
            <Button
              variant="secondary"
              icon={ArrowLeft}
              mobileIconOnly
              className="shrink-0"
              onClick={() => navigate('/ogcl-transfer-requests')}
            >
              Back
            </Button>
            {showChecklist && canCreateMlSheet(user) && (
              <Button
                variant="primary"
                icon={Truck}
                disabled={!allUnloaded}
                className="flex-1 sm:flex-none min-w-0"
                onClick={() => navigate('/ml-sheets/add')}
              >
                Create ML / Dispatch
              </Button>
            )}
            {canReview && request.status === 'PENDING' && (
              <>
                <Button
                  variant="primary"
                  icon={CheckCircle2}
                  disabled={acting}
                  className="flex-1 sm:flex-none min-w-0"
                  onClick={() => openReview('approve')}
                >
                  Approve
                </Button>
                <Button
                  variant="danger"
                  icon={XCircle}
                  disabled={acting}
                  className="flex-1 sm:flex-none min-w-0"
                  onClick={() => openReview('reject')}
                >
                  Reject
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-5 min-w-0">
          <h2 className="text-base sm:text-lg font-semibold text-secondary mb-3 sm:mb-4">Request Details</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4 min-w-0">
            <DetailItem label="From Hub">{request.from_hub_name || request.from_hub_id}</DetailItem>
            <DetailItem label="To Hub">{request.to_hub_name || request.to_hub_id}</DetailItem>
            <DetailItem label="Shipments">{request.shipment_count ?? shipments.length}</DetailItem>
            <DetailItem label="Total Freight">{formatMoney(totalFreight)}</DetailItem>
            <DetailItem label="Vehicle">
              {request.vehicle_number}
              {request.is_external_vehicle ? ' (Other)' : ''}
            </DetailItem>
            <DetailItem label="Driver">{request.driver_name}</DetailItem>
            <DetailItem label="Requested By">
              {request.requested_by_display
                || formatUsernameLocation(
                  request.requested_by_name || request.requested_by_username,
                  request.requested_by_hub_name,
                  request.requested_by
                )}
            </DetailItem>
            <DetailItem label="Created At">{formatDateTime(request.created_at)}</DetailItem>
            <DetailItem label="Reviewed At">{formatDateTime(request.reviewed_at)}</DetailItem>
            <DetailItem label="Reviewed By">
              {request.reviewer_display
                || formatUsernameLocation(
                  request.reviewer_name || request.reviewer_username,
                  request.reviewer_hub_name,
                  request.reviewer_user_id || '—'
                )}
            </DetailItem>
            <DetailItem label="Remarks">{request.remarks}</DetailItem>
            <DetailItem label="Review Remarks">{request.review_remarks}</DetailItem>
          </div>
        </div>

        {showChecklist && (
          <div className="bg-white rounded-xl shadow-sm p-3 sm:p-5 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
              <h2 className="text-base sm:text-lg font-semibold text-secondary flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 shrink-0" />
                Unload checklist
              </h2>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mb-3">
              Unload with a note before dispatch. Revert after unload puts the GC back to pending unload.
              Remove takes it off this ITCL request. Unload cannot be changed after an ML sheet is created.
            </p>
            <ul className="space-y-2">
              {shipments.map((shipment) => (
                <li
                  key={shipment.shipment_id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-secondary break-all">{shipment.shipment_id}</p>
                    <p className="text-xs text-gray-500 break-words">
                      {shipment.sender_location || '—'} → {shipment.receiver_location || '—'}
                    </p>
                    {shipment.action_note ? (
                      <p className="text-xs text-gray-500 mt-1 break-words">Note: {shipment.action_note}</p>
                    ) : null}
                  </div>
                  {shipment.reverted ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-800 shrink-0">
                      Reverted
                    </span>
                  ) : shipment.unloaded ? (
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
                        <CheckCircle2 className="w-4 h-4" /> Unloaded
                      </span>
                      <ItclShipmentActionsMenu
                        row={shipment}
                        role={role}
                        menuKey={`check-${shipment.shipment_id}`}
                        openMenuId={openMenuId}
                        disabled={unloadingId != null}
                        onToggle={() => setOpenMenuId(
                          openMenuId === `check-${shipment.shipment_id}` ? null : `check-${shipment.shipment_id}`
                        )}
                        onClose={() => setOpenMenuId(null)}
                        onUnload={() => markUnloaded(shipment.shipment_id)}
                        onNote={() => markNote(shipment.shipment_id)}
                        onRevert={() => markReverted(shipment.shipment_id)}
                        onRemove={() => markRemoved(shipment.shipment_id)}
                      />
                    </div>
                  ) : (
                    <ItclShipmentActionsMenu
                      row={shipment}
                      role={role}
                      menuKey={`check-${shipment.shipment_id}`}
                      openMenuId={openMenuId}
                      disabled={unloadingId != null}
                      onToggle={() => setOpenMenuId(
                        openMenuId === `check-${shipment.shipment_id}` ? null : `check-${shipment.shipment_id}`
                      )}
                      onClose={() => setOpenMenuId(null)}
                      onUnload={() => markUnloaded(shipment.shipment_id)}
                      onNote={() => markNote(shipment.shipment_id)}
                      onRevert={() => markReverted(shipment.shipment_id)}
                      onRemove={() => markRemoved(shipment.shipment_id)}
                    />
                  )}
                </li>
              ))}
            </ul>
            {!allUnloaded && (
              <p className="mt-3 text-xs text-amber-800">
                Dispatch is enabled after every shipment on this request is unloaded.
              </p>
            )}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm overflow-hidden min-w-0">
          <div className="px-3 sm:px-5 py-3 sm:py-4 border-b border-gray-100 flex items-center justify-between gap-3 min-w-0">
            <h2 className="text-base sm:text-lg font-semibold text-secondary truncate">Shipments</h2>
            <span className="text-xs sm:text-sm text-gray-500 shrink-0">
              {shipments.length} item{shipments.length === 1 ? '' : 's'}
            </span>
          </div>

          {!shipments.length ? (
            <div className="px-4 py-12 text-center text-gray-500 text-sm">
              No shipments linked to this transfer request.
            </div>
          ) : (
            <>
              <div className="hidden md:block w-full max-w-full overflow-x-auto overscroll-x-contain">
                <table className="w-full min-w-[880px] text-sm">
                  <thead className="bg-gray-50 text-left text-gray-600">
                    <tr>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">Shipment</th>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">From → To</th>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">Sender / Receiver</th>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">Payment</th>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">Freight</th>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">Status</th>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">ITCL</th>
                      <th className="px-3 lg:px-4 py-3 whitespace-nowrap">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {shipments.map((shipment) => (
                      <tr key={shipment.shipment_id} className="align-top hover:bg-gray-50/80">
                        <td className="px-3 lg:px-4 py-3 font-medium text-secondary break-all">{shipment.shipment_id}</td>
                        <td className="px-3 lg:px-4 py-3 break-words">
                          {shipment.sender_location || '—'} → {shipment.receiver_location || '—'}
                        </td>
                        <td className="px-3 lg:px-4 py-3 break-words">
                          <div>{shipment.sender_name || '—'}</div>
                          <div className="text-gray-500">{shipment.receiver_name || '—'}</div>
                        </td>
                        <td className="px-3 lg:px-4 py-3 whitespace-nowrap">{paymentLabel(shipment.mode_of_payment)}</td>
                        <td className="px-3 lg:px-4 py-3 whitespace-nowrap">{formatMoney(shipment.freight_charges)}</td>
                        <td className="px-3 lg:px-4 py-3">
                          <span className={`inline-flex px-2 py-1 rounded-full border text-xs font-semibold ${badgeClass(shipment.status)}`}>
                            {shipment.status || '—'}
                          </span>
                        </td>
                        <td className="px-3 lg:px-4 py-3">
                          <span className={`inline-flex px-2 py-1 rounded-full border text-xs font-semibold ${badgeClass(shipment.ogcl_transfer_status)}`}>
                            {shipment.ogcl_transfer_status || '—'}
                          </span>
                        </td>
                        <td className="px-3 lg:px-4 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            {shipment.status !== 'NOT_FOUND' && (
                              <Button
                                size="sm"
                                variant="secondary"
                                icon={Eye}
                                mobileIconOnly
                                onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                              >
                                View
                              </Button>
                            )}
                            {showChecklist ? (
                              <ItclShipmentActionsMenu
                                row={shipment}
                                role={role}
                                menuKey={`table-${shipment.shipment_id}`}
                                openMenuId={openMenuId}
                                disabled={unloadingId != null}
                                onToggle={() => setOpenMenuId(
                                  openMenuId === `table-${shipment.shipment_id}`
                                    ? null
                                    : `table-${shipment.shipment_id}`
                                )}
                                onClose={() => setOpenMenuId(null)}
                                onUnload={() => markUnloaded(shipment.shipment_id)}
                                onNote={() => markNote(shipment.shipment_id)}
                                onRevert={() => markReverted(shipment.shipment_id)}
                                onRemove={() => markRemoved(shipment.shipment_id)}
                              />
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden p-3 space-y-3">
                {shipments.map((shipment) => (
                  <div
                    key={shipment.shipment_id}
                    className="rounded-xl border border-gray-100 border-l-4 border-l-orange-400 bg-white shadow-sm p-3 min-w-0"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="min-w-0">
                        <div className="font-bold text-secondary text-sm break-all">{shipment.shipment_id}</div>
                        <p className="text-xs text-gray-600 mt-1 break-words">
                          {shipment.sender_location || '—'} → {shipment.receiver_location || '—'}
                        </p>
                      </div>
                      <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-semibold shrink-0 ${badgeClass(shipment.status)}`}>
                        {shipment.status || '—'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                      <div className="min-w-0">
                        <span className="text-gray-500 block">Sender</span>
                        <p className="font-medium text-gray-800 break-words">{shipment.sender_name || '—'}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-gray-500 block">Receiver</span>
                        <p className="font-medium text-gray-800 break-words">{shipment.receiver_name || '—'}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-gray-500 block">Payment</span>
                        <p className="font-medium text-gray-800">{paymentLabel(shipment.mode_of_payment)}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-gray-500 block">Freight</span>
                        <p className="font-medium text-gray-800">{formatMoney(shipment.freight_charges)}</p>
                      </div>
                      <div className="min-w-0 col-span-2">
                        <span className="text-gray-500 block">ITCL</span>
                        <span className={`inline-flex mt-0.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${badgeClass(shipment.ogcl_transfer_status)}`}>
                          {shipment.ogcl_transfer_status || '—'}
                        </span>
                      </div>
                    </div>
                    {shipment.status !== 'NOT_FOUND' && (
                      <div className="pt-3 border-t border-gray-100 flex flex-wrap justify-end gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          icon={Eye}
                          onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                        >
                          View
                        </Button>
                        {showChecklist ? (
                          <ItclShipmentActionsMenu
                            row={shipment}
                            role={role}
                            menuKey={`mobile-${shipment.shipment_id}`}
                            openMenuId={openMenuId}
                            disabled={unloadingId != null}
                            onToggle={() => setOpenMenuId(
                              openMenuId === `mobile-${shipment.shipment_id}`
                                ? null
                                : `mobile-${shipment.shipment_id}`
                            )}
                            onClose={() => setOpenMenuId(null)}
                            onUnload={() => markUnloaded(shipment.shipment_id)}
                            onNote={() => markNote(shipment.shipment_id)}
                            onRevert={() => markReverted(shipment.shipment_id)}
                            onRemove={() => markRemoved(shipment.shipment_id)}
                          />
                        ) : null}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <OgclReviewConfirmModal
        open={reviewModal.open}
        action={reviewModal.action}
        requestId={request.request_id}
        loading={acting}
        onClose={closeReview}
        onConfirm={confirmReview}
      />
      <ItclActionNoteModal
        open={noteModal.open}
        action={noteModal.action}
        shipmentCount={noteModal.shipmentIds?.length || 0}
        loading={unloadingId != null}
        onClose={() => unloadingId == null && setNoteModal({ open: false, action: 'unload', shipmentIds: [] })}
        onConfirm={confirmNoteAction}
      />
    </DashboardLayout>
  );
};

export default ViewOgclTransferRequest;
